import React, { useEffect, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useSelector } from 'react-redux';
import { getFocusedRouteNameFromRoute, useRoute } from '@react-navigation/native';

import ColorWrapper, { WithColor } from '@styles/colors';

import { fullHeight, fullWidth, scaleOP } from '@components/frame';
import { fontOP } from '@styles/fonts';

import { getMachineBackgroundByState, isWeb, machineDetailsConditions } from '@helpers';
import OperatorPanelInformationNavigation from './OperatorPanelInformationNavigation';
import OperatorPanelInformationTabs from './OperatorPanelInformationTabs';
import { selectSelectedItem } from '@reducers/machines';
import { resetTabsCounts } from '@reducers/operatorPanel';
import { getBoxShadowStyle } from '@styles/shadow';
import {
  BOTTOM_SHEET_TOP_OFFSET,
  BOTTOM_SHEET_TOP_OFFSET_WEB,
  OP_INFO_TABS_WIDTH,
  SCREEN_BORDER_RADIUS,
} from '@v2/pages/common/constants/base.constants';
import { useAppDispatch } from '@store/hooks/useAppDispatch';

interface OperatorPanelInformationProps extends WithColor {}

const OperatorPanelInformation: React.FC<OperatorPanelInformationProps> = ({ C }) => {
  const dispatch = useAppDispatch();
  const route = useRoute();

  const selectedTab = getFocusedRouteNameFromRoute(route);
  const selectedMachine = useSelector(selectSelectedItem);

  // Reset counts when component unmounts
  useEffect(() => {
    return () => {
      dispatch(resetTabsCounts());
    };
  }, [resetTabsCounts]);

  const isLight = C.scheme === 'light';
  const mainBg = C(`Background/${isLight ? 'Light' : 'Dark'}/Panel [mobile]`, false);

  const statusBarBg = useMemo(() => {
    const { ignoredNcFile, machineActive, machineOffline } = machineDetailsConditions({
      status: selectedMachine?.machineStatus?.status,
      ncFileStatus: selectedMachine?.machineStatus?.ncFileStatus,
      subStatus: selectedMachine?.machineStatus?.subStatus,
      ncFileExist: !!selectedMachine?.machineStatus?.ncFile,
    });

    return C(getMachineBackgroundByState({ ignoredNcFile, machineActive, machineOffline, status: selectedMachine?.machineStatus?.status }), false);
  }, [selectedMachine]);

  return (
    <>
      {isWeb ? (
        <View
          style={{
            position: 'absolute',
            top: 0,
            width: '100%',
            height: BOTTOM_SHEET_TOP_OFFSET,
            backgroundColor: statusBarBg,
          }}
        />
      ) : null}
      <View
        style={[
          {
            backgroundColor: mainBg,
            height: fullHeight - BOTTOM_SHEET_TOP_OFFSET_WEB,
            position: 'absolute',
            top: isWeb ? BOTTOM_SHEET_TOP_OFFSET_WEB : BOTTOM_SHEET_TOP_OFFSET / 2,
            width: fullWidth,
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
            borderTopLeftRadius: scaleOP(SCREEN_BORDER_RADIUS),
            borderTopRightRadius: scaleOP(SCREEN_BORDER_RADIUS),
            overflow: 'hidden',
          },
          isWeb && getBoxShadowStyle({ type: 'Ultra Wide' }),
        ]}
      >
        <View
          style={{
            maxWidth: scaleOP(OP_INFO_TABS_WIDTH),
            width: '100%',
            height: '100%',
            borderRightWidth: StyleSheet.hairlineWidth,
            borderRightColor: C('Hairline/Light/Transpar [mobile]'),
          }}
        >
          <OperatorPanelInformationTabs font={fontOP} scale={scaleOP} selectedTab={selectedTab ?? ''} />
        </View>
        <View style={{ flex: 1, height: '100%', flexDirection: 'column' }}>
          <OperatorPanelInformationNavigation C={C} />
        </View>
      </View>
    </>
  );
};

export default ColorWrapper(OperatorPanelInformation);
